import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  clearImpersonation,
  hasImpersonationRecord,
  IMPERSONATION_STORAGE_KEY,
  isImpersonationExpired,
  type ImpersonationState,
  READ_ONLY_BLOCKED_EVENT,
  readImpersonation,
  saveImpersonation,
} from "@/lib/impersonation";
import { logger, serializeError } from "@/lib/logger";

interface ImpersonationContextType {
  state: ImpersonationState | null;
  isSwitching: boolean;
  startImpersonation: (personId: number, fallbackName: string) => Promise<void>;
  returnToAdmin: () => Promise<void>;
}

type StartResponse = {
  hashed_token: string;
  display_name: string;
};

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(
  undefined,
);

export const ImpersonationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ImpersonationState | null>(() =>
    readImpersonation(),
  );
  const [isSwitching, setIsSwitching] = useState(false);

  const returnToAdmin = useCallback(async () => {
    if (isSwitching) return;
    setIsSwitching(true);
    try {
      const { error } = await supabase.auth.signOut({ scope: "local" });
      if (error) throw error;

      clearImpersonation();
      setState(null);
      queryClient.clear();
      window.location.assign("/");
    } catch (error) {
      logger.error("Could not sign out of view-as mode", serializeError(error));
      toast({
        title: "Please try signing out again.",
        variant: "destructive",
      });
    } finally {
      setIsSwitching(false);
    }
  }, [isSwitching, queryClient]);

  const startImpersonation = useCallback(
    async (personId: number, fallbackName: string) => {
      if (isSwitching) return;
      setIsSwitching(true);
      try {
        const { data, error } = await supabase.functions.invoke<StartResponse>(
          "admin-impersonate",
          { body: { person_id: personId } },
        );
        if (error || !data?.hashed_token) {
          throw error ?? new Error("This person is not available for view-as.");
        }

        const targetName = data.display_name || fallbackName;
        saveImpersonation(targetName);
        setState(readImpersonation());

        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: data.hashed_token,
          type: "magiclink",
        });
        if (verifyError) throw verifyError;
        // Another tab may have exited view-as while verifyOtp was in flight;
        // never keep a participant session without the guard and banner.
        if (!hasImpersonationRecord()) {
          await supabase.auth.signOut({ scope: "local" });
          throw new Error("View-as was cancelled in another tab.");
        }

        queryClient.clear();
        window.location.assign("/");
      } catch (error) {
        clearImpersonation();
        setState(null);
        throw error;
      } finally {
        setIsSwitching(false);
      }
    },
    [isSwitching, queryClient],
  );

  useEffect(() => {
    const handleReadOnlyBlocked = () => {
      const current = readImpersonation();
      if (current) {
        toast({
          title: `Read-only while viewing as ${current.targetName}`,
          variant: "destructive",
        });
      }
    };
    window.addEventListener(READ_ONLY_BLOCKED_EVENT, handleReadOnlyBlocked);
    return () =>
      window.removeEventListener(
        READ_ONLY_BLOCKED_EVENT,
        handleReadOnlyBlocked,
      );
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === IMPERSONATION_STORAGE_KEY) {
        setState(readImpersonation());
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    if (!state) return;
    const remaining = state.expiresAt - Date.now();
    if (remaining <= 0) {
      void returnToAdmin();
      return;
    }
    const timer = window.setTimeout(() => void returnToAdmin(), remaining);
    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        isImpersonationExpired(state)
      ) {
        void returnToAdmin();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [returnToAdmin, state]);

  return (
    <ImpersonationContext.Provider
      value={{ state, isSwitching, startImpersonation, returnToAdmin }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => {
  const context = useOptionalImpersonation();
  if (!context) {
    throw new Error(
      "useImpersonation must be used within an ImpersonationProvider",
    );
  }
  return context;
};

export const useOptionalImpersonation = () => useContext(ImpersonationContext);
